import { usePsfPromoteOrSharePageData } from '#src/app/pages/psf/promoteOrShare/pageContext';

import { useArrivalChannel } from './useArrivalChannel';

export type ChannelAwareCtaChannel = keyof typeof FCM_CHANNEL_CTA_LAYOUTS;

/**
 * Which of the two share-sheet layouts a channel's CTA renders with:
 * - `cta_only`: the channel CTA is the sole action button, Copy link moves into the grid.
 * - `cta_with_secondary_copy`: the channel CTA sits above Copy link, demoted to a knockout secondary.
 *
 * The Top-8 relaunch (CHANGE-99290) re-tests both designs on both channels, one per treatment arm, so
 * neither channel has a settled layout right now. The fixed values below only apply to the productized
 * plane, which is switched off in every country for the duration of the relaunch.
 */
export type ChannelAwareCtaLayout = 'cta_only' | 'cta_with_secondary_copy';

export type ChannelAwareCta = Readonly<{
	channel: ChannelAwareCtaChannel;
	layout: ChannelAwareCtaLayout;
}> | null;

// Channels the productized FCM (`psf_promote_or_share_channel_aware_cta`) governs, with their fixed
// layout.
const FCM_CHANNEL_CTA_LAYOUTS = {
	facebook: 'cta_only',
	whatsapp: 'cta_with_secondary_copy',
} as const satisfies Record<string, ChannelAwareCtaLayout>;

function isFcmCtaChannel(channel: string): channel is keyof typeof FCM_CHANNEL_CTA_LAYOUTS {
	return channel in FCM_CHANNEL_CTA_LAYOUTS;
}

/**
 * Channel-aware share CTA: signers who arrived through a share on a live channel (`current_medium`)
 * get a prominent "Share on <channel>" primary CTA on the share slide-up, matched to their arrival
 * channel.
 *
 * Two planes can put it on screen. The productized plane is enabled per-country by the
 * `psf_promote_or_share_channel_aware_cta` FCM (also its kill-switch: empty list = off) and ships the
 * fixed layout above. The experiment plane is the Top-8 relaunch (CHANGE-99290), one experiment per
 * channel, each re-testing both layouts — so both `whatsappCtaLayout` and `facebookCtaLayout` carry the
 * specific layout their treated arm resolved to, and are `null` for control arms, ineligible signers,
 * or the channel's own FCM being off.
 *
 * For the duration of the relaunch the productized plane is empty in every country, so in practice
 * only the experiment branches fire. The productized plane is kept wired for when a winner ships.
 *
 * The experiment checks are an OR, not a replacement: a country already in the productized channel list
 * keeps its CTA whatever arm the signer draws. That way a mis-scoped experiment config can never
 * withdraw a shipped feature — it can only ever add the CTA where the FCM is silent.
 *
 * ⚠️ Killing a channel's CTA takes BOTH switches whenever its experiment runs against a country the
 * productized FCM also serves: emptying `psf_promote_or_share_channel_aware_cta` alone still leaves
 * treated signers seeing it. Disable the channel's own FCM
 * (`psf_promote_or_share_facebook_cta_intl` / `psf_promote_or_share_whatsapp_cta`) too.
 *
 * Returns which channel's CTA to render and the layout it ships, or `null` (production sheet).
 */
export function useChannelAwareCta(
	whatsappCtaLayout: ChannelAwareCtaLayout | null,
	facebookCtaLayout: ChannelAwareCtaLayout | null,
): ChannelAwareCta {
	const {
		data: { channelAwareCtaChannels },
	} = usePsfPromoteOrSharePageData();
	const arrivalChannel = useArrivalChannel();
	// Checked before the FCM narrowing below: placement is functional, not cosmetic — the generic check
	// at the bottom returns `null` outright for any channel not in `channelAwareCtaChannels`, so an
	// experiment branch placed after it would never be reached for the exact countries it exists to
	// serve. `!includes` is belt and braces: the treat hooks already refuse to enroll a country the
	// productized FCM serves, so this can't be reached today. Kept so the render layer alone guarantees
	// a productized country can never be pulled into an experiment arm by a config slip upstream.
	if (arrivalChannel === 'whatsapp' && whatsappCtaLayout && !channelAwareCtaChannels.includes('whatsapp')) {
		return { channel: 'whatsapp', layout: whatsappCtaLayout };
	}
	if (arrivalChannel === 'facebook' && facebookCtaLayout && !channelAwareCtaChannels.includes('facebook')) {
		return { channel: 'facebook', layout: facebookCtaLayout };
	}
	if (!arrivalChannel || !isFcmCtaChannel(arrivalChannel)) return null;
	if (!channelAwareCtaChannels.includes(arrivalChannel)) return null;
	return { channel: arrivalChannel, layout: FCM_CHANNEL_CTA_LAYOUTS[arrivalChannel] };
}
